import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Image as ImageIcon, Trash2, Upload, Download, Loader2, BookOpen,
} from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { toast } from "sonner";

// Get a usable backend-absolute URL for the raw file so <img> tags can render.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const ACCEPT = ".pdf,.docx,.txt,.png,.jpg,.jpeg";

function fileTypeIcon(note) {
  if (note.is_image) return ImageIcon;
  return FileText;
}

function humanSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 900) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

export default function SermonNotesCard() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const canUpload = user?.role === "admin" || user?.role === "pastoral";

  const load = async () => {
    try {
      const { data } = await api.get("/sermon-notes");
      setNotes(data);
    } catch {
      // Silent — this card is secondary; don't bail the whole home page.
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onPick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    let failed = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post("/sermon-notes", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        failed++;
        toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
    e.target.value = "";
    load();
  };

  const remove = async (note) => {
    if (!confirm(`Delete "${note.original_name}"?`)) return;
    try {
      await api.delete(`/sermon-notes/${note.id}`);
      toast.success("Deleted");
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to delete");
    }
  };

  const fileUrl = (note) => `${BACKEND}/api/sermon-notes/${note.id}/file`;

  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="sermon-notes-card">
      <div className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap border-b border-brand-primary/10">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-peach/30 grid place-items-center shrink-0">
            <BookOpen className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">This week's sermon notes</p>
            <h3 className="font-heading text-xl text-brand-primary mt-0.5">Shared files from the pastoral team</h3>
            <p className="text-xs text-brand-primary/55 mt-1">Auto-clears every Monday at 12:00 AM Central.</p>
          </div>
        </div>
        {canUpload && (
          <div className="shrink-0">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              onChange={onFiles}
              className="hidden"
              data-testid="sermon-notes-file-input"
            />
            <Button
              onClick={onPick}
              disabled={uploading}
              data-testid="sermon-notes-upload-button"
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-10 px-5 shadow-warm"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-1.5" />
              )}
              Upload files
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-brand-primary/55" data-testid="sermon-notes-loading">
          <Loader2 className="h-5 w-5 mx-auto animate-spin text-terracotta/60" />
        </div>
      ) : notes.length === 0 ? (
        <div className="p-10 text-center" data-testid="sermon-notes-empty">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-cream grid place-items-center border border-brand-primary/10">
            <FileText className="h-6 w-6 text-brand-primary/50" strokeWidth={1.6} />
          </div>
          <p className="font-medium text-brand-primary mt-4">No sermon notes posted this week yet.</p>
          <p className="text-xs text-brand-primary/55 mt-1">
            {canUpload
              ? "Upload PDFs, DOCX, TXT, or images to share with staff."
              : "Check back once a pastor uploads this week's files."}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-brand-primary/10" data-testid="sermon-notes-list">
          {notes.map((note) => {
            const Icon = fileTypeIcon(note);
            return (
              <li
                key={note.id}
                className="p-5 flex items-center gap-4 hover:bg-cream/40 transition"
                data-testid={`sermon-note-${note.id}`}
              >
                {note.is_image ? (
                  <a
                    href={fileUrl(note)}
                    target="_blank"
                    rel="noreferrer"
                    className="h-14 w-14 rounded-xl bg-cream overflow-hidden border border-brand-primary/10 shrink-0 grid place-items-center"
                    title="Open full image in new tab"
                    data-testid={`sermon-note-thumb-${note.id}`}
                  >
                    <img
                      src={fileUrl(note)}
                      alt={note.original_name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <div className="h-14 w-14 rounded-xl bg-cream border border-brand-primary/10 shrink-0 grid place-items-center">
                    <Icon className="h-6 w-6 text-terracotta" strokeWidth={1.6} />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <a
                    href={fileUrl(note)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-primary hover:text-terracotta truncate inline-block max-w-full"
                    data-testid={`sermon-note-open-${note.id}`}
                  >
                    {note.original_name}
                  </a>
                  <p className="text-xs text-brand-primary/55 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{note.uploader_name}</span>
                    <span>·</span>
                    <span>{formatDistanceToNow(parseISO(note.created_at), { addSuffix: true })}</span>
                    {note.size_bytes ? (
                      <>
                        <span>·</span>
                        <span>{humanSize(note.size_bytes)}</span>
                      </>
                    ) : null}
                    {note.is_image && (
                      <Badge className="rounded-full bg-sage/20 text-brand-primary border-0 text-[10px] uppercase tracking-wider">
                        image
                      </Badge>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={fileUrl(note)}
                    target="_blank"
                    rel="noreferrer"
                    className="h-9 w-9 rounded-lg grid place-items-center text-brand-primary/60 hover:text-terracotta hover:bg-cream"
                    title="Open / download"
                    data-testid={`sermon-note-download-${note.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  {(user?.role === "admin" || note.uploaded_by === user?.user_id) && (
                    <button
                      onClick={() => remove(note)}
                      data-testid={`sermon-note-delete-${note.id}`}
                      className="h-9 w-9 rounded-lg grid place-items-center text-brand-primary/60 hover:text-terracotta hover:bg-cream"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
